/**
 * Sizing guide — shared by the standalone page and the product drawer.
 *
 * The drawer holds no content of its own: it clones the standalone page, so
 * sizes and steps are edited in one place. Defined in a shared file so cloned
 * nodes upgrade in the drawer exactly as they do on the page.
 */
(() => {
  const UNIT_KEY = 'ldl:sizing-unit';

  // Storage can throw in private mode or with site data blocked, and the
  // guide must still work — the unit choice just stops being remembered.
  const readUnit = () => {
    try {
      return localStorage.getItem(UNIT_KEY);
    } catch (error) {
      return null;
    }
  };

  const writeUnit = (unit) => {
    try {
      localStorage.setItem(UNIT_KEY, unit);
    } catch (error) {
      /* not fatal */
    }
  };

  if (!customElements.get('ldl-sizing-guide')) {
    customElements.define('ldl-sizing-guide', class extends HTMLElement {
      connectedCallback() {
        const saved = readUnit();
        const unit = saved === 'cm' || saved === 'in' ? saved : (this.dataset.defaultUnit || 'cm');
        this.apply(unit);

        const group = this.querySelector('[data-units]');
        if (!group) return;

        // The toggle only exists once it can do something.
        group.hidden = false;

        group.querySelectorAll('[data-unit]').forEach((button) => {
          button.addEventListener('click', () => {
            const next = button.dataset.unit;
            writeUnit(next);
            // Every open instance moves together — the page behind the drawer included.
            document.querySelectorAll('ldl-sizing-guide').forEach((el) => el.apply && el.apply(next));
          });
        });
      }

      apply(unit) {
        this.dataset.unit = unit;
        this.querySelectorAll('[data-unit]').forEach((button) => {
          button.setAttribute('aria-pressed', button.dataset.unit === unit ? 'true' : 'false');
        });
      }
    });
  }

  if (!customElements.get('ldl-sizing-drawer')) {
    customElements.define('ldl-sizing-drawer', class extends HTMLElement {
      connectedCallback() {
        this.trigger = document.getElementById(this.getAttribute('trigger-id'));
        this.target = this.querySelector('[data-sizing-target]');
        this.source = this.getAttribute('source-url');
        if (!this.trigger || !this.target || !this.source) return;

        this.loaded = false;

        // Fetch on intent rather than on open, so the content is usually
        // already there by the time the drawer finishes sliding in.
        ['pointerenter', 'focus'].forEach((type) => {
          this.trigger.addEventListener(type, () => this.load(), { once: true });
        });
        // The dialog carries its own open state; read that rather than
        // inferring it from the trigger.
        this.dialog = this.closest('x-drawer') || document.getElementById(this.trigger.getAttribute('aria-controls'));

        this.trigger.addEventListener('click', () => this.load());

        // Keep the hash in step with the drawer. x-drawer can be opened and
        // dismissed several ways — trigger, close button, scrim, Escape — and
        // it re-parents this element as it opens, which defeats listeners and
        // observers bound to either node. Reading its state on a slow timer is
        // the one approach that holds for every case; each tick is two string
        // comparisons and changes nothing unless the drawer actually moved.
        this.watcher = setInterval(() => this.syncHash(), 300);

        if (window.location.hash === '#sizing') {
          // Let the drawer component finish wiring itself up first.
          requestAnimationFrame(() => this.trigger.click());
        }

        this.dialogMissing = !this.dialog;
      }

      disconnectedCallback() {
        clearInterval(this.watcher);
      }

      get isOpen() {
        return this.dialog ? this.dialog.hasAttribute('open') : false;
      }

      // Deep-linkable while open; a refresh after closing must not reopen it.
      syncHash() {
        const { pathname, search, hash } = window.location;
        if (this.isOpen && hash !== '#sizing') {
          history.replaceState(null, '', `${pathname}${search}#sizing`);
        } else if (!this.isOpen && hash === '#sizing') {
          history.replaceState(null, '', `${pathname}${search}`);
        }
      }

      async load() {
        if (this.loaded) return;
        this.loaded = true;

        try {
          const response = await fetch(this.source, { headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error(`${response.status}`);

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const content = doc.querySelector('[data-ldl-sizing-content]');
          if (!content) throw new Error('no sizing content on the source page');

          content.classList.add('ldl-sizing--drawer');
          this.target.replaceChildren(content);
        } catch (error) {
          // Never strand the shopper: fall back to the real page.
          this.loaded = false;
          this.target.innerHTML = '';
          const link = document.createElement('a');
          link.className = 'link';
          link.href = this.source;
          link.textContent = this.getAttribute('fallback-text') || 'Open the sizing guide';
          this.target.appendChild(link);
        }
      }
    });
  }
})();
